import { Location } from '@angular/common';
import { inject, Injectable } from '@angular/core';

import { firstValueFrom, Observable } from 'rxjs';
import { first, switchMap, tap } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { V1_AuthService } from '@app/api/v1/auth/v1-auth.service';
import { isSloneekApiRequest, SloneekOriginHeader, sloneekOriginHeaderValue } from '@app/auth/interceptors/sloneek-origin.utils';
import { logoutActions } from '@app/auth/store/actions/logout.actions';
import { refreshTokenActions } from '@app/auth/store/actions/refresh-token.actions';
import { selectAccessToken, selectRefreshToken } from '@app/auth/store/selectors/token.selectors';
import { FetchInterceptor, FetchRequest } from '@app/shared/models/fetch-interceptor';
import { ToastHandlerService } from '@app/shared/services/toast-handler.service';

@Injectable()
export class FetchAuthInterceptor implements FetchInterceptor {
  private readonly location = inject(Location);
  private readonly store = inject(Store);
  private readonly v1_AuthService = inject(V1_AuthService);
  private readonly toastService = inject(ToastHandlerService);

  private tokenRefreshed$: Observable<unknown> | null = null;

  public async intercept(request: FetchRequest, next: (req: FetchRequest) => Promise<Response>): Promise<Response> {
    try {
      const token = await firstValueFrom(this.store.select(selectAccessToken));
      const response = await this.handleRequest(request, next, token);

      if (!response.ok && response.status === 401) {
        const error = await response.json().catch(() => ({}));

        if (error?.hash_expired) {
          this.store.dispatch(logoutActions.logoutWithStandardRedirect());
          throw new Error('Hash expired');
        }

        // try to refresh the token
        return this.handle401Error(request, next);
      }

      if (response instanceof Response && response.status === 422) {
        const body = await response.json();
        this.toastService.showErrorToast(body.detail[0]?.msg);
      }

      if ([401, 403, 404, 503].includes(response.status)) {
        const body = await response
          .clone()
          .json()
          .catch(() => ({ detail: response.statusText }));
        this.toastService.showErrorToast(`${response.status} - ${body.detail}`);
      }

      return response;
    } catch (error) {
      if (error instanceof Response && error.status === 401) {
        return this.handle401Error(request, next);
      } else {
        this.toastService.showErrorToast(error.message);
      }
    }
  }

  private async handleRequest(
    request: FetchRequest,
    next: (req: FetchRequest) => Promise<Response>,
    token: string | null,
  ): Promise<Response> {
    const init = request.init || {};
    const headers = new Headers(init.headers || {});

    if (isSloneekApiRequest(request.url)) {
      headers.set(SloneekOriginHeader, sloneekOriginHeaderValue(this.location.path()));
    }

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return next({
      ...request,
      init: {
        ...init,
        headers,
      },
    });
  }

  private async handle401Error(request: FetchRequest, next: (req: FetchRequest) => Promise<Response>): Promise<Response> {
    if (!this.tokenRefreshed$) {
      this.tokenRefreshed$ = this.store.select(selectRefreshToken).pipe(
        first(),
        switchMap(refreshToken =>
          this.v1_AuthService.refreshToken({ refresh_token: refreshToken ?? '' }).pipe(
            tap({
              next: response => this.store.dispatch(refreshTokenActions.success({ response: response.data })),
              error: () => this.store.dispatch(refreshTokenActions.error()),
            }),
          ),
        ),
      );
    }

    try {
      await firstValueFrom(this.tokenRefreshed$);
      this.tokenRefreshed$ = null;
      return this.intercept(request, next);
    } catch (error) {
      this.tokenRefreshed$ = null;
      this.store.dispatch(logoutActions.logoutWithStandardRedirect());
      throw error;
    }
  }
}
